// Creates the page's one viewport (the wasm engine, else the mock) and connects it to the store.
// winit allows one event loop per page, so this is a singleton: React StrictMode's double mount
// gets the same promise.

import type { Viewport, ViewportEvent } from './api.ts';
import { API_MAJOR } from './api.ts';
import { MockViewport } from './mock.ts';
import { createWgpuViewport, type NoViewport } from './wgpu.ts';
import { dispatch } from './events.ts';
import { useAtlas } from '../app/store.ts';
import { connectBridge } from './bridge.ts';
import { progressUpdate } from './progress.ts';

let instance: Promise<Viewport> | null = null;

function onEvent(e: ViewportEvent) {
  dispatch(e);
  const s = useAtlas.getState();
  switch (e.ev) {
    case 'ready':
      if (Number.parseInt(e.api, 10) !== API_MAJOR) s.setEngine({ status: 'error', error: `viewport API ${e.api}, the app speaks ${API_MAJOR}.x` });
      else s.setEngine({ status: 'ready', adapter: e.adapter, version: e.engine });
      break;
    case 'progress': {
      const up = progressUpdate(s, e);
      if (up) useAtlas.setState(up);
      break;
    }
    case 'hover':
      s.hover(e.id, 'viewport');
      break;
    case 'click':
      if (e.button !== 0) break;
      if (e.count >= 2 && e.id) {
        s.select(e.id);
        s.camera({ frame: { id: e.id } });
      } else s.select(e.id);
      break;
    case 'stats':
      s.setEngine({ fps: e.fps, frameMs: e.frameMs });
      break;
    case 'error':
      if (e.fatal) s.setEngine({ status: 'error', error: e.message });
      else console.warn('[viewport]', e.message);
      break;
    default:
      break;
  }
}

export function getViewport(): Promise<Viewport> | null {
  return instance;
}

export function startViewport(canvas: HTMLCanvasElement, opts: { forceMock?: boolean } = {}): Promise<Viewport> {
  if (instance) return instance;
  const options = { assets: '/', onEvent, maxDpr: 2, reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches };
  instance = (async () => {
    const made = opts.forceMock
      ? null
      : await createWgpuViewport(canvas, options).catch((err: unknown): NoViewport => {
          console.error('[viewport] wgpu failed, using the mock', err);
          return { why: 'failed', detail: err instanceof Error ? err.message : String(err) };
        });
    // The 2D preview stands in where the wasm viewport can't run, and the page says why (GpuNotice).
    const wgpu = made && !('why' in made) ? made : null;
    const vp = wgpu ?? (await MockViewport.create(canvas, options));
    useAtlas.getState().setEngine({ kind: wgpu ? 'wgpu' : 'mock', ...(made && 'why' in made ? { fallback: made } : {}) });
    connectBridge(vp);
    return vp;
  })();
  return instance;
}
